import { Bar, BarChart, ResponsiveContainer, XAxis } from "recharts";
import { chartTheme } from "@/pages/dealFlow/metrics/chartTheme";
import type { MonthlyCountPoint } from "@/api/buyers";
import { monthLabel } from "./tileBits";

/**
 * Twelve trailing months, as bars.
 *
 * <p><b>No period selector, deliberately.</b> A trailing year is the window that
 * answers "is intake growing"; a control offering four answers to one question
 * is four chances to read the wrong one, and it puts a piece of state on the
 * page that has to be explained to whoever the link is sent to.</p>
 *
 * <p>Months with no rows arrive as ZEROES from the server rather than being
 * omitted. A gap in a bar chart reads as "we have no data for August"; a zero
 * bar reads as "nobody arrived in August", and only one of those is what the
 * series means.</p>
 *
 * <p>🔴 The month key is a WALL-CALENDAR value and is formatted by
 * <c>monthLabel</c>, which parses the literal <c>yyyy-MM</c> string. Putting it
 * through the timezone-aware date formatter renders June as May for every reader
 * west of Greenwich — a defect class this codebase has already found three
 * times.</p>
 */
export function VolumeTile({
  title,
  points,
  loading,
}: {
  title: string;
  points: MonthlyCountPoint[];
  loading?: boolean;
}) {
  const data = points.map((p) => ({ ...p, label: monthLabel(p.month) }));
  const total = data.reduce((sum, p) => sum + p.count, 0);

  return (
    <section aria-label={title} data-testid="volume-trail">
      <h3 className="font-display text-label font-semibold uppercase tracking-label text-gray-mid m-0 mb-space-2">
        {title}
      </h3>
      {loading || data.length === 0 ? (
        <div className="h-[72px] rounded-md bg-white-warm" aria-hidden />
      ) : (
        <>
          <ResponsiveContainer width="100%" height={72}>
            <BarChart data={data} margin={{ top: 4, right: 0, bottom: 0, left: 0 }}>
              <XAxis
                dataKey="label"
                stroke={chartTheme.axis}
                fontSize={9}
                tickLine={false}
                axisLine={false}
                interval={1}
              />
              <Bar dataKey="count" fill={chartTheme.primary} radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
          {/* The series total, so the shape can be read against a magnitude — a
              bar chart with no scale says which month was busiest and nothing
              about whether that meant four or four hundred. */}
          <p className="m-0 text-caption text-gray-mid tabular-nums">
            {total} in the last 12 months
          </p>
        </>
      )}
    </section>
  );
}
